import { BKJButtonIconTextInput } from '@bookjane2/bookjane-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import { JaneGridSelect } from 'components/JaneGridSelect';
import { QuerySwitch } from 'components/QuerySwitch';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import {
  EstimatedTotalPayoutFooterText,
  RequestTeamMemberWrapper,
  SendToAgenciesViewTitle,
  HeaderWrapper,
  SendToAgenciesViewTitleContainer,
  TotalAvailableJanesText,
  ConfirmationButtons,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesView.styled';
import { FC, Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { AutoMargin } from 'components/AutoMargin';
import { RequestTeamMemberFilterBar } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/RequestTeamMemberFilterBar';
import { PriceFilter } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/RequestTeamMemberView.styled';
import { useSendToAgenciesPropsContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.props.context';
import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import {
  FetchTeamMembersDataContext,
  useFetchTeamMembersDataContext,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/contexts/FetchTeamMembers.data.context';
import { useFetchMinMaxPricesByPositionIdDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/contexts/FetchMinMaxPricesByPositionId.data.context';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import {
  parsePrice,
  priceFormatter,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesSummaryView/SendToAgenciesSummaryView.constants';
import { FormatCurrency } from 'components/FormatCurrency';

function RenderJanes() {
  const { onChange: onChangeForm, values } = useSendToAgenciesFormContext();
  const { loadMore } = useFetchTeamMembersDataContext();

  const onChange = (e: UseFormBehaviorsEventType) => {
    if (e.target.name === 'marketplace_jane_id') {
      onChangeForm([
        { target: { name: 'marketplace_jane_id', value: e.target.value } },
        { target: { name: 'jane_id', value: '' } },
      ]);
    } else {
      onChangeForm([
        { target: { name: 'jane_id', value: e.target.value } },
        { target: { name: 'marketplace_jane_id', value: '' } },
      ]);
    }
  };

  return (
    <JaneGridSelect
      edgeCaseType="SendToAgencies"
      paginationType="InfiniteScroll"
      type="Single"
      name="jane_id"
      dataPath="requestAllJanes.nodes"
      value={[values.jane_id || values.marketplace_jane_id]}
      context={FetchTeamMembersDataContext}
      onChange={onChange}
      loadMore={loadMore}
      pageInfoDataPath="requestAllJanes.pageInfo"
    />
  );
}

const RenderSSFControls: FC = () => {
  const { t } = useTranslation();
  const { data } = useFetchMinMaxPricesByPositionIdDataContext();
  const { values, onChange } = useFetchTeamMembersDataContext();

  const { min, max } = data;

  const [isOpen, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  return (
    <Flex flexDirection="column">
      <BKJButtonIconTextInput
        placeholder={t('shift_modal:SEARCH_MEMBER')}
        buttonLabel={t('shift_modal:FILTERS')}
        name="byMultipleFields"
        iconName="Search"
        buttonIcon="Filters"
        value={values.byMultipleFields}
        onChange={onChange}
        onButtonClick={handleClick}
        autoComplete="off"
        label=""
        required={false}
        error={''}
        disabled={false}
      />
      <RequestTeamMemberFilterBar isOpen={isOpen}>
        <PriceFilter
          priceRange={[min, max]}
          name="byRateLessThanOrEqualTo"
          value={values.byRateLessThanOrEqualTo}
          onChange={onChange}
        />
      </RequestTeamMemberFilterBar>
    </Flex>
  );
};

export function RequestTeamMemberViewComponent() {
  const { t } = useTranslation();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { values } = useSendToAgenciesFormContext();
  const { goToNextView } = useSendToAgenciesPropsContext();
  const { data: allJanesData } = useFetchTeamMembersDataContext();

  const janeId = values.jane_id || values.marketplace_jane_id;

  const selectedJane = allJanesData?.requestAllJanes?.nodes?.find(
    (jane) => String(jane.id) === String(janeId),
  );

  const estimatedPayout = selectedJane
    ? selectedJane.positions?.[0]?.agencyDetails?.openRate ||
      selectedJane.positions?.[0]?.communityDetails?.chargeRate?.toString()
    : undefined;

  const estimatedTotalPayout = estimatedPayout
    ? priceFormatter.format(order!.totalDuration! * parsePrice(estimatedPayout))
    : '';

  const totalCount = allJanesData?.requestAllJanes?.totalCount || 0;

  return (
    <Fragment>
      <HeaderWrapper>
        <SendToAgenciesViewTitleContainer alignItems="center" justifyContent="space-between">
          <SendToAgenciesViewTitle css="margin-bottom: 0;">
            {t('shift_modal:REQUEST_AGENCY_MEMBER')}
          </SendToAgenciesViewTitle>
          {allJanesData && (
            <TotalAvailableJanesText>
              {`${totalCount} ${t('shift_modal:AVAILABLE')}`}
            </TotalAvailableJanesText>
          )}
        </SendToAgenciesViewTitleContainer>

        <RenderSSFControls />
      </HeaderWrapper>

      <RequestTeamMemberWrapper
        flexDirection="column"
        padding="12px 32px 0 32px"
        gap="4px"
        flex="1"
        height="100%"
        width="100%"
      >
        <QuerySwitch component={RenderJanes} context={FetchTeamMembersDataContext} />
      </RequestTeamMemberWrapper>
      <BKJSideModalFooter>
        <EstimatedTotalPayoutFooterText disabled={!estimatedTotalPayout}>
          {t('shift_modal:ESTIMATED_PAYOUT')}:
          <FormatCurrency amount={`${estimatedTotalPayout || '0.00'}`} />
        </EstimatedTotalPayoutFooterText>
        <AutoMargin />
        <ConfirmationButtons
          width="180px"
          variant="PurpleSolid"
          onClick={goToNextView}
          disabled={!values.jane_id && !values.marketplace_jane_id}
        >
          {t('shift_modal:NEXT')}
        </ConfirmationButtons>
      </BKJSideModalFooter>
    </Fragment>
  );
}
